'use client';

import { NotFoundIframeContent } from '@/app/iframes/components/NotFoundIframeContent';
import { ToolTip } from '@/components/atoms/ToolTip/ToolTip';
import { BatteryHealthBar } from '@/components/molecules/BatteryHealthBar/BatteryHealthBar';
import Footer from '@/components/template/SavingsToolV2/Footer';
import { OverlayWrapper } from '@/components/template/SavingsToolV2/Overlays';
import { getBatteryWarrantyRating } from '@/lib/report';
import { TrimSpecs } from '@/lib/schema/build/types';
import { VehicleType } from '@/lib/schema/inventory/types';
import { InformationCircleIcon } from '@heroicons/react/24/solid';
import { Suspense, useState } from 'react';

interface BatteryHealthIframeProps {
  product: VehicleType | null;
  trimSpecs?: TrimSpecs | null;
}

const BatteryHealthComponent = ({
  product,
  trimSpecs,
}: BatteryHealthIframeProps) => {
  const [isOverlayOpen, setIsOverlayOpen] = useState(false);
  const { remainingYears, remainingMileage } = getBatteryWarrantyRating({
    batteryWarranty: trimSpecs?.batteryWarranty,
    mileage: product?.mileage,
    year: product?.year,
  });
  const warrantyString =
    remainingYears || remainingMileage
      ? `Remaining: ${remainingYears ? `${remainingYears * 12} months${remainingMileage ? ', or' : ''}` : ''} ${remainingMileage ? `${remainingMileage.toLocaleString()} miles` : ''}`
      : 'N/A';

  return (
    <div className="min-h-iframe flex flex-col overflow-hidden rounded-small bg-white">
      <div className="flex flex-wrap items-center justify-between bg-brandMidnight px-xs py-s sm:px-s sm:py-l md:px-m lg:px-xl text-white">
        <div className="text-body1SemiBold sm:text-h4SemiBold">
          Battery health{' '}
          {product?.evScore && (
            <span className="text-brandElectric">{product?.evScore}*</span>
          )}
        </div>
      </div>
      {!product && !isOverlayOpen && (
        <NotFoundIframeContent type="battery health" />
      )}
      {product && !isOverlayOpen && (
        <div className="flex flex-1 flex-col gap-s sm:gap-l p-xs sm:p-s md:p-l lg:p-xl">
          <div className="text-body2Bold sm:text-body1Bold text-neutral-900">
            {product?.year} {product?.make} {product?.model}
          </div>
          <BatteryHealthBar
            degradationPercentage={
              product?.batteryInfo.batteryHealthEstimate ?? null
            }
          />
          <div className="flex flex-col justify-between py-xs gap-xs sm:gap-0 sm:flex-row sm:py-s">
            <div className="flex gap-xs items-start">
              <div className="whitespace-nowrap text-microRegular sm:text-body1Regular text-neutral-900">
                Battery Warranty
              </div>
              <ToolTip
                aria-label="Battery warranty information tooltip"
                hoverElement={
                  <InformationCircleIcon className="mr-xs size-[16px] sm:size-[20px] flex-shrink-0" />
                }
              >
                U.S. federal regulation requires electric car batteries to have
                a minimum eight-year or 100,000-mile warranty
              </ToolTip>
            </div>
            <div className="text-microLight sm:text-body1Light text-neutral-900">
              {warrantyString}
            </div>
          </div>
        </div>
      )}
      {isOverlayOpen && (
        <div className="flex-auto">
          <OverlayWrapper
            onClose={() => setIsOverlayOpen(false)}
            title="EV Battery Health"
          >
            <div className="flex flex-col gap-s sm:gap-l overflow-y-auto px-xs py-xs sm:px-s sm:pb-s md:px-l">
              <div className="text-microRegular sm:text-body2Regular text-neutral-800">
                The battery score is calculated by considering the car&apos;s
                age and mileage to determine how much the battery has degraded
                over time. This score helps estimate the remaining health and
                efficiency of the electric vehicle&apos;s battery.
              </div>
            </div>
          </OverlayWrapper>
        </div>
      )}
      <Footer
        overlayOpen={isOverlayOpen}
        onClickDisclaimerPrompt={() => setIsOverlayOpen(!isOverlayOpen)}
      />
    </div>
  );
};

const BatteryHealthIframe = (props: BatteryHealthIframeProps) => (
  <Suspense>
    <BatteryHealthComponent {...props} />
  </Suspense>
);

export default BatteryHealthIframe;
